import React, { useEffect, useRef, useState } from "react";
import AuthTeams from "../../auth/AuthTeams";
import { getApiClient } from "../../redux/api/client";
import { appInsights } from "../../azure/appInsights";
import { IconButton } from "@fluentui/react/lib/Button";
import {
  Label,
  Stack,
  PrimaryButton,
  CommandButton,
  Icon,
} from "@fluentui/react";
import "../../css/styles.css";
import { resolveAdvSearchCellValue } from "../../functions/objectConnectionSearchUtils";
import { getAdvSearchRowCellValue } from "../../functions/advSearchWhereClause";
import Tooltip from "../Tooltip/Tooltip";

import {
  ERROR_TYPE,
  ERROR_DETAILS,
  IFS_SERVER_ERROR,
  NO_LOGICALUNIT_FOUND,
  ADVANCED_SEARCH_COLUMNS_ERROR,
  ADVANCED_SEARCH_FIELD_HELP_TOOLTIP,
  IFS_TOKEN_EXPIRED,
} from "../../redux/constants/messageTypes";
import {
  OBJECT_SELECT_TYPE,
  ADV_RESULTS_STATUS,
  DATA_LOAD_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  TIME_OUT_STATUS_CODE,
  PROGRESS_STATUS
} from "../../redux/constants/metaData";
import { useTheme } from "../../theme/OAS365Theme";
import AdvSearchObjectFilter from "./AdvSearchObjectFilter";
import AdvSearchObjectResults from "./AdvSearchObjectResults";
import Loader from "../LoadSpinner";
import { Dialog, DialogType } from "@fluentui/react/lib/Dialog";
import { useId } from "@fluentui/react-hooks";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import UserAuth from "../../auth/userAuth";
import {
  isTokenRefreshFailed,
  isTokenRefreshRequired,
  getUpdatedTokenRefreshPreAction,
  isValidPlayload,
} from "../../functions/reduxMethod";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { useLogin } from "../../redux/hooks/useLogin";

const AdvSearchObjectSearch = () => {
  const {
    searchLuName,
    whereClause,
    advObjConSelectedRows,
    advSearchSelectedObjs,
    recentObjectsSelected,
    advObjConColumns,
    mruKeyNames,
  } = useCheckin();
  const resultsSearchRef = useRef(null);
  const filterRef = useRef(null);
  const {
    searchAdvanceObjectHide,
    advObjResultsStatus,
    advSearchMedatDataStatus,
    tokenRefreshPreActionInvoke,
  } = useMetadata();
  const {
    onAdvObjConColumnsChange,
    onMruKeyNamesChange,
    onSelectedObjectConnectionChanged,
    onAdvObjCompeleted,
    onAdvObjSearchModelHide,
    onAdvObjSearchModelSelected,
    onAdvObjRowSelected,
    onWhereClauseChange,
    onAdvObjResultsStatusChanged,
    onAdvObjMetaDataStatusChanged,
    onTokenAccessFailed,
    onLoginRefreshPreActionClear,
    onLoginRefreshPreActionLoad,
    onServerErrorMessageInvoke,
    onExceptionMessageInvoke,
    onErrorMessageInvoke,
  } = useTools();
  const { onIfsUserLogin } = useLogin();

  const dataLoadingStatus = {
    completed: "Completed",
    loading: "Loading",
    nodata: "NoData",
    error: "Error",
  };
  let userAuth = new UserAuth();

  async function loadObjectColumns(
    tokenRefreshPreAction = {
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.advanceSearchObjectColumnssLoad,
      refreshResponse: RESPONSE_TYPE.none,
    },
  ) {
    setDataLoad(dataLoadingStatus.loading);
    let appInsightProps = {
      userId: new AuthTeams().getEmail(),
      functionName: "_onGetRecentObjects",
      statusCode: 0,
      selectedRows: null,
    };
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
      if (!tokenOneDrive) {
        onTokenAccessFailed(tokenResponse);
        return;
      }

      if (isTokenRefreshFailed(tokenRefreshPreAction)) {
        onLoginRefreshPreActionClear();
        setDataLoad(dataLoadingStatus.error);
        let messageBox = {
          type: ERROR_TYPE,
          message: IFS_TOKEN_EXPIRED,
        };
        setErrorMessage(messageBox.message);
        onErrorMessageInvoke(messageBox);
        return;
      }

      appInsights.trackTrace({
        message: "Get Class with Format",
        appInsightProps,
      });

      const apiClient = getApiClient(tokenOneDrive);
      const res = await apiClient.get(
        `api/DocumentManagement/get-objectconnectioncolumnsinfo?webUser=${encodeURIComponent(
          new AuthTeams().getEmail(),
        )}&luName=${encodeURIComponent(searchLuName.luName)}&viewName=${encodeURIComponent(
          searchLuName?.viewName ?? "",
        )}`,
      );

      if (res.status === 200) {
        const data = getLogicalUnits(res.data);
        onAdvObjConColumnsChange(data);
        const mruData = getMruKeyNames(res.data);
        onMruKeyNamesChange(mruData);
        setDataLoad(dataLoadingStatus.completed);
        onAdvObjMetaDataStatusChanged(DATA_LOAD_STATUS.completed);
      } else {
        appInsightProps.statusCode = res.status;
        setErrorMessage(NO_LOGICALUNIT_FOUND);
        if (res.status === 204) {
          setDataLoad(dataLoadingStatus.nodata);
          appInsights.trackException({
            error: `${NO_LOGICALUNIT_FOUND}`,
            appInsightProps,
          });
        } else {
          setDataLoad(dataLoadingStatus.error);
          appInsights.trackException({
            error: `${IFS_SERVER_ERROR}`,
            errorMessage: res.data,
            appInsightProps,
          });
          setErrorMessage(IFS_SERVER_ERROR);
        }
      }
      onLoginRefreshPreActionClear();
    } catch (error) {
      console.log(error?.toJSON?.() ?? error, "err");
      if (
        isTokenRefreshRequired(
          error.response,
          tokenRefreshPreAction,
          REFRESH_TOKEN_ACTION.advanceSearchObjectColumnssLoad,
        )
      ) {
        const updatedPreAction = getUpdatedTokenRefreshPreAction(
          tokenRefreshPreAction,
          REFRESH_TOKEN_ACTION.advanceSearchObjectColumnssLoad,
        );
        onLoginRefreshPreActionLoad(updatedPreAction);
        try {
          await onIfsUserLogin(true, updatedPreAction);
        } catch (refreshErr) {
          console.log(refreshErr?.message ?? refreshErr, "IFS token refresh failed");
          onLoginRefreshPreActionClear();
          setDataLoad(dataLoadingStatus.error);
          setErrorMessage(IFS_TOKEN_EXPIRED);
        }
      } else if (!error?.response) {
        onLoginRefreshPreActionClear();
        setDataLoad(dataLoadingStatus.error);
        const msg = error?.message ?? String(error);
        setErrorMessage(`${ERROR_DETAILS} ${msg}`);
        onExceptionMessageInvoke({ message: msg, showMessageBox: true });
      } else {
        const serverResponseError = {
          response: error.response,
          statusCode: error.response?.status || TIME_OUT_STATUS_CODE,
          error: error,
          showMessageBox: true,
        };
        setDataLoad(dataLoadingStatus.error);
        onLoginRefreshPreActionClear();
        setErrorMessage(ADVANCED_SEARCH_COLUMNS_ERROR);
        onServerErrorMessageInvoke(serverResponseError);
      }
    }
  }

  const [dataLoad, setDataLoad] = useState(dataLoadingStatus.loading);
  const [errorMessage, setErrorMessage] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const labelId = useId("dialogLabel");
  const subTextId = useId("subTextLabel");
  const [newWidth, setNewWidth] = useState(350);
  const handlerResize = () => {
    setNewWidth(window.outerWidth - 250);
  };

  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: true,
    }),
    [labelId, subTextId],
  );

  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();

  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const scrollbarSize = 10;

  const advSearchScrollbarSelectors = {
    scrollbarWidth: "thin",
    scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
    selectors: {
      "&::-webkit-scrollbar": {
        width: scrollbarSize,
        height: scrollbarSize,
      },
      "&::-webkit-scrollbar-track": {
        background: scrollbarTrack,
      },
      "&::-webkit-scrollbar-thumb": {
        background: scrollbarThumb,
        borderRadius: scrollbarSize,
        border: `2px solid ${scrollbarTrack}`,
        minHeight: 40,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: isDarkMode ? "#9a9a9a" : "#b3b3b3",
      },
      "&::-webkit-scrollbar-button": {
        display: "none",
        height: 0,
        width: 0,
      },
    },
  };

  useEffect(() => {
    const styleId = "adv-search-dialog-scrollbar";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer {
        scrollbar-width: thin;
        scrollbar-color: ${scrollbarThumb} ${scrollbarTrack};
      }
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer::-webkit-scrollbar {
        width: ${scrollbarSize}px;
        height: ${scrollbarSize}px;
      }
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer::-webkit-scrollbar-track {
        background: ${scrollbarTrack};
      }
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb {
        background: ${scrollbarThumb};
        border-radius: ${scrollbarSize}px;
        border: 2px solid ${scrollbarTrack};
        min-height: 40px;
      }
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb:hover {
        background: ${isDarkMode ? "#9a9a9a" : "#b3b3b3"};
      }
      .oas-adv-search-dialog .ms-ScrollablePane--contentContainer::-webkit-scrollbar-button,
      .oas-adv-search-dialog .adv-search-filters-scroll::-webkit-scrollbar-button {
        display: none;
        height: 0;
        width: 0;
      }
      .oas-adv-search-dialog .adv-search-filters-scroll {
        scrollbar-width: thin;
        scrollbar-color: ${scrollbarThumb} ${scrollbarTrack};
      }
      .oas-adv-search-dialog .adv-search-filters-scroll::-webkit-scrollbar {
        width: ${scrollbarSize}px;
        height: ${scrollbarSize}px;
      }
      .oas-adv-search-dialog .adv-search-filters-scroll::-webkit-scrollbar-track {
        background: ${scrollbarTrack};
      }
      .oas-adv-search-dialog .adv-search-filters-scroll::-webkit-scrollbar-thumb {
        background: ${scrollbarThumb};
        border-radius: ${scrollbarSize}px;
        border: 2px solid ${scrollbarTrack};
        min-height: 40px;
      }
      .oas-adv-search-dialog .adv-search-filters-scroll::-webkit-scrollbar-thumb:hover {
        background: ${isDarkMode ? "#9a9a9a" : "#b3b3b3"};
      }
    `;
  }, [scrollbarTrack, scrollbarThumb, scrollbarSize, isDarkMode]);

  const dialogStyles = {
    main: {
      backgroundColor: theme.colors.backgroundColor,
      width: "min(96vw, 1200px)",
      maxWidth: "96vw",
      minHeight: "88vh",
      maxHeight: "98vh",
      height: "90vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    content: {
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
    },
  };

  useEffect(() => {
    setNewWidth(window.outerWidth);
    window.addEventListener("resize", handlerResize);
    return () => {
      window.removeEventListener("resize", handlerResize);
    };
  }, []);

  useEffect(() => {
    if (advSearchMedatDataStatus === DATA_LOAD_STATUS.onLoad) {
      loadObjectColumns();
      return;
    }

    if (
      advSearchMedatDataStatus === DATA_LOAD_STATUS.completed &&
      Array.isArray(advObjConColumns) &&
      advObjConColumns.length > 0
    ) {
      setDataLoad(dataLoadingStatus.completed);
    }
  }, [advSearchMedatDataStatus, advObjConColumns]);

  useEffect(() => {
    if (triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) === true) {
      const run = async () => {
        switch (tokenRefreshPreActionInvoke.actionType) {
          case REFRESH_TOKEN_ACTION.advanceSearchObjectColumnssLoad:
            loadObjectColumns(tokenRefreshPreActionInvoke);
            break;
          default:
            console.log(
              "No action for token refresh pre-action invoke: " +
              tokenRefreshPreActionInvoke.actionType,
            );
            break;
        }
      };
      run();
    }
  }, [tokenRefreshPreActionInvoke]);

  const bodyHeight = (document.documentElement.offsetHeight / 100) * 80;
  const binIcon = { iconName: "Refresh" };

  const dialogContentProps = {
    type: DialogType.largeHeader,
    closeButtonAriaLabel: "Close",
    className: "custom-large-header",
  };

  const loaderBorderStyle = {
    padding: "10px autopx",
    bottom: "50px",
    marginTop: "20px",
    height: bodyHeight,
  };

  const dialogLoaderStyle = {
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "calc(90vh - 80px)",
    height: "100%",
    boxSizing: "border-box",
  };
  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };
  const selectedCount = Array.isArray(advObjConSelectedRows)
    ? advObjConSelectedRows.length
    : 0;

  function onClose() {
    onAdvObjRowSelected(null);
    onWhereClauseChange("");
    filterRef.current?.resetFilters?.();
    onAdvObjSearchModelHide(true);
    setShowMoreFilters(false);
  }

  function click() {
    const clause =
      filterRef.current?.getWhereClause?.() ?? whereClause ?? "";
    const filters = filterRef.current?.getFilterItems?.() ?? [];
    onWhereClauseChange(clause);
    resultsSearchRef.current?.executeSearch(clause, filters);
  }

  function disbaleSelectButton() {
    return !(
      advObjConSelectedRows !== undefined &&
      advObjConSelectedRows !== null &&
      advObjConSelectedRows.length > 0
    );
  }

  function selectedAdvanceObjects() {
    let selectedAdvObjs = Array.isArray(advSearchSelectedObjs)
      ? [...advSearchSelectedObjs]
      : [];
    if (
      advObjConSelectedRows !== undefined &&
      advObjConSelectedRows !== null &&
      advObjConSelectedRows.length > 0
    ) {
      for (let i = 0; i < advObjConSelectedRows.length; i++) {
        let selectedAdvObj = {
          keyDesc: advObjConSelectedRows[i].keyDesc,
          keyRef: advObjConSelectedRows[i].keyRef,
          luName: advObjConSelectedRows[i].luName,
          nameDesc: advObjConSelectedRows[i].nameDesc,
          docObjDesc: advObjConSelectedRows[i].docObjDesc,
          selectType: OBJECT_SELECT_TYPE.advance,
        };
        let isExit = false;
        if (
          selectedAdvObjs !== undefined &&
          selectedAdvObjs !== null &&
          selectedAdvObjs.length > 0
        ) {
          for (let j = 0; j < selectedAdvObjs.length; j++) {
            if (
              selectedAdvObjs[j].luName === selectedAdvObj.luName &&
              selectedAdvObjs[j].keyRef === selectedAdvObj.keyRef
            ) {
              isExit = true;
              j = selectedAdvObjs.length;
            }
          }
        }
        if (!isExit) {
          selectedAdvObjs.push(selectedAdvObj);
        }
      }
    }
    onSelectedObjectConnectionChanged(
      Array.isArray(recentObjectsSelected) ? [...recentObjectsSelected] : [],
      selectedAdvObjs
    );
    onAdvObjCompeleted(true);
    onClose();
    onAdvObjSearchModelSelected(true);
  }

  function triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) {
    if (isValidPlayload(tokenRefreshPreActionInvoke)) {
      return (
        tokenRefreshPreActionInvoke.actionType ===
        REFRESH_TOKEN_ACTION.advanceSearchObjectColumnssLoad
      );
    }

    return false;
  }

  const toggleFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  const renderSeeMoreButtonText = () => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {showMoreFilters ? "See less" : "See more"}
      <Icon
        iconName={showMoreFilters ? "ChevronUp" : "ChevronDown"}
        styles={{
          root: {
            color: theme.colors.addovation70Green,
            fontSize: 12,
          },
        }}
      />
    </span>
  );

  switch (dataLoad) {
    case dataLoadingStatus.completed:
      return (
        <>
          <Dialog
            className="oas-adv-search-dialog"
            hidden={searchAdvanceObjectHide}
            onDismiss={() => onClose()}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            minWidth={Math.max(900, Math.min(newWidth - 300, window.innerWidth - 48))}
            styles={dialogStyles}
          >
            <Stack
              enableScopedSelectors
              tokens={{ childrenGap: 5 }}
              styles={{
                root: {
                  flex: "1 1 auto",
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "calc(98vh - 48px)",
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <Stack.Item
                align="center"
                style={{ height: 35, display: "flex", alignItems: "center", gap: 6 }}
              >
                <span
                  style={{
                    fontFamily: theme.fonts.regular,
                    fontSize: theme.fontSizes.medium,
                    color: theme.colors.fontColor,
                  }}
                >
                  <strong style={{ fontWeight: "bold" }}>
                    Advanced Search
                  </strong>{" "}
                  - {searchLuName.nameDesc}
                </span>
                <Tooltip
                  content={ADVANCED_SEARCH_FIELD_HELP_TOOLTIP}
                  position="bottom"
                  delay={100}
                  gapSpace={0.5}
                  maxWidth={420}
                  listType="div"
                >
                  <Icon
                    iconName="Info"
                    ariaLabel="Advanced search field help"
                    styles={{
                      root: {
                        fontSize: 12,
                        color: theme.colors.addovation70Green,
                        cursor: "default",
                        position: "relative",
                        top: -7,
                        left: -3,
                      },
                    }}
                  />
                </Tooltip>
              </Stack.Item>

              <Stack.Item
                disableShrink
                className={showMoreFilters ? "adv-search-filters-scroll" : undefined}
                styles={{
                  root: {
                    marginTop: "25px",
                    flexShrink: 0,
                    minHeight: 0,
                    maxHeight: showMoreFilters ? "20vh" : undefined,
                    overflowY: showMoreFilters ? "auto" : "visible",
                    overflowX: "hidden",
                    position: "relative",
                    zIndex: 2,
                    ...(showMoreFilters ? advSearchScrollbarSelectors : {}),
                  },
                }}
              >
                <Stack
                  horizontal={!showMoreFilters}
                  horizontalAlign={showMoreFilters ? "stretch" : "center"}
                  verticalAlign="end"
                  tokens={{ childrenGap: 12 }}
                  styles={{
                    root: {
                      width: "100%",
                    },
                  }}
                >
                  <div
                    style={
                      showMoreFilters
                        ? { width: "100%" }
                        : {
                            flex: "0 1 680px",
                            width: "100%",
                            maxWidth: "680px",
                            minWidth: "260px",
                          }
                    }
                  >
                    <AdvSearchObjectFilter
                      ref={filterRef}
                      showMoreFilters={showMoreFilters}
                      forceSingleRow={!showMoreFilters}
                    />
                  </div>
                  {!showMoreFilters && (
                    <Stack
                      horizontal
                      tokens={{ childrenGap: 10 }}
                      verticalAlign="end"
                      styles={{ root: { padding: "15px" } }}
                    >
                      {advObjConColumns && advObjConColumns.length > 3 && (
                        <CommandButton
                          onClick={toggleFilters}
                          style={styleTheme.SeeMorePrimaryButtonFillStyle}
                          onRenderText={renderSeeMoreButtonText}
                        />
                      )}
                      <PrimaryButton
                        text="Search"
                        style={styleTheme.PrimaryButtonFillStyle}
                        onClick={click}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack.Item>

              {showMoreFilters && (
                <Stack.Item
                  disableShrink
                  styles={{
                    root: {
                      marginTop: "16px",
                      flexShrink: 0,
                      minHeight: "fit-content",
                      overflow: "visible",
                    },
                  }}
                >
                  <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    styles={{
                      root: {
                        width: "100%",
                        padding: "15px",
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    <Stack horizontal verticalAlign="center">
                      {advObjConColumns && advObjConColumns.length > 3 && (
                        <CommandButton
                          onClick={toggleFilters}
                          style={styleTheme.SeeMorePrimaryButtonFillStyle}
                          onRenderText={renderSeeMoreButtonText}
                        />
                      )}
                    </Stack>
                    <PrimaryButton
                      text="Search"
                      style={styleTheme.PrimaryButtonFillStyle}
                      onClick={click}
                    />
                  </Stack>
                </Stack.Item>
              )}

              <Stack.Item
                grow
                styles={{
                  root: {
                    minHeight: 0,
                    overflow: "hidden",
                    flex: "1 1 auto",
                    display: "flex",
                    flexDirection: "column",
                  },
                }}
              >
                <AdvSearchObjectResults ref={resultsSearchRef} />
              </Stack.Item>

              <Stack.Item
                disableShrink
                align="end"
                styles={{
                  root: {
                    marginTop: "auto",
                    flexShrink: 0,
                  },
                }}
              >
                <div
                  style={{
                    
                    boxSizing: "border-box",
                                  paddingBottom: "60px",
                                  paddingTop:"20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Stack horizontal tokens={{ childrenGap: 5 }}>
                    <PrimaryButton
                      text="Cancel"
                      style={styleTheme.CancelButtonFillStyle}
                      onClick={() => onClose()}
                    />
                    <PrimaryButton
                      text={selectedCount > 0 ? `Select (${selectedCount})` : "Select"}
                      style={
                        disbaleSelectButton() === true
                          ? styleTheme.DisableButtoneStyle
                          : styleTheme.PrimaryButtonFillStyle
                      }
                      disabled={disbaleSelectButton()}
                      onClick={selectedAdvanceObjects}
                    />
                  </Stack>
                </div>
              </Stack.Item>
            </Stack>
          </Dialog>
        </>
      );
    case dataLoadingStatus.loading:
      return (
        <>
          <Dialog
            className="oas-adv-search-dialog"
            hidden={searchAdvanceObjectHide}
            onDismiss={() => onClose()}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            minWidth={Math.max(900, Math.min(newWidth - 300, window.innerWidth - 48))}
            styles={dialogStyles}
          >
            <div style={dialogLoaderStyle}>
              <div style={{ width: 50, height: 50 }}>
                <Loader />
              </div>
            </div>
          </Dialog>
        </>
      );
    case dataLoadingStatus.nodata:
    case dataLoadingStatus.error:
      return (
        <>
          <Dialog
            className="oas-adv-search-dialog"
            hidden={searchAdvanceObjectHide}
            onDismiss={() => onClose()}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            minWidth={Math.max(900, Math.min(newWidth - 300, window.innerWidth - 48))}
            styles={dialogStyles}
          >
            <Stack
              enableScopedSelectors
              tokens={{ childrenGap: 5 }}
              styles={{
                root: {
                  flex: "1 1 auto",
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "calc(98vh - 48px)",
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <Stack.Item
                grow
                styles={{
                  root: {
                    minHeight: 0,
                    overflow: "auto",
                    flex: "1 1 auto",
                  },
                }}
              >
                <div style={loaderBorderStyle}>
                  <div style={messageBorderStyle}>
                    <IconButton
                      style={{ marginLeft: "80px" }}
                      iconProps={binIcon}
                      title="Refresh"
                      ariaLabel="Refresh"
                      onClick={(ev) => {
                        loadObjectColumns();
                      }}
                    />
                    <Label style={styleTheme.LabelErrorMessageStyle}>
                      {dataLoad === dataLoadingStatus.nodata ? NO_LOGICALUNIT_FOUND : errorMessage}
                    </Label>
                  </div>
                </div>
              </Stack.Item>

              <Stack.Item
                disableShrink
                align="end"
                styles={{
                  root: {
                    marginTop: "auto",
                    flexShrink: 0,
                  },
                }}
              >
                <div
                  style={{
                    height: "90px",
                    boxSizing: "border-box",
                    paddingBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Stack horizontal tokens={{ childrenGap: 5 }}>
                    <PrimaryButton
                      text="Cancel"
                      style={styleTheme.CancelButtonFillStyle}
                      onClick={() => onClose()}
                    />
                  </Stack>
                </div>
              </Stack.Item>
            </Stack>
          </Dialog>
        </>
      );
    default:
      return <></>;
  }
};

function getLogicalUnits(logicalUnits) {
  let arr = [];

  try {
    const lableList = logicalUnits.lovColPrompts.split("^");
    const lableTypeList = logicalUnits.lovColTypes.split("^");
    const colHeaderList = logicalUnits.lovColNames.split(",");

    for (let i = 0; i < lableList.length; i++) {
      const fieldName = (colHeaderList[i] ?? "").trim();
      arr.push({
        key: "column" + i + 1,
        name: (lableList[i] ?? "").trim(),
        fieldName,
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        dataType: (lableTypeList[i] ?? "").trim(),
        onRender: (item) => {
          const text = resolveAdvSearchCellValue(
            getAdvSearchRowCellValue(item, fieldName),
          );
          return <span>{text}</span>;
        },
      });
    }
  } catch (ex) {
    console.log(ex.message, "err");
  }

  return arr;
}

function getMruKeyNames(data) {
  let arr = [];
  try {
    const mruKeyNames = data.mruKeyNames.split(",");
    for (let i = 0; i < mruKeyNames.length; i++) {
      const key = (mruKeyNames[i] ?? "").trim();
      arr.push({
        key,
        name: key,
      });
    }
  } catch (ex) {
    console.log(ex.message, "err");
  }
  return arr;
}

export default AdvSearchObjectSearch;
